import React, {useContext} from "react";
import {View} from "react-native";
import Div from "../lib/Div";
import P from "../lib/P";
import {StoreContext} from "./StoreContextProvider";
import FontAwesome from "@expo/vector-icons/FontAwesome";
import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import {Bet} from "../../generated/smg_service_pb";
import {useConfirmBeforeEachTurn} from "../hooks/useConfirmBeforeEachTurn";
import {isShowRotationDebugPanel} from "../utils/devOverrides";

// isNonZeroInt64 normalises proto3 int64 values across the three shapes a
// TS-proto generator might emit (bigint for protobuf-es, string for some
// codecs, Long for ts-proto) into a "set" boolean. A truthy check alone
// would accept the string "0" because strings are truthy, so we coerce
// through Number() before comparing to zero.
function isNonZeroInt64(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === 'bigint') return v !== 0n;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return v !== '' && Number(v) !== 0;
  // Long-like fallback: {low, high} object.
  const asNumber = Number(v as never);
  return Number.isFinite(asNumber) && asNumber !== 0;
}

type PlayerInfo = {
  id: number;
  username: string;
};

// Single source of truth for the "unknown participant" display fallback.
// When usernames lag the playerOrder (the projector pads short slices with
// '' — see roundRobinProjection.ts) this returns "Player <id>" so the user
// sees a stable identifier rather than a blank field. Localize / rebrand
// this label here, not at every call site.
const playerLabel = (player: PlayerInfo | undefined, id: number): string =>
  player?.username || `Player ${id}`;

type Props = {
  players: PlayerInfo[];
  // Current user's active bet, if any. Used to source target distance and the
  // next-shot target preview from the authoritative source (Paylines/Bet)
  // rather than storing duplicated state on the round-robin status.
  bet?: Bet;
};

export const RoundRobinTurnDisplay = ({players, bet}: Props) => {
  const Context = useContext(StoreContext);
  const {roundRobinStatus, user, userSessionData} = Context;
  // Lock-glyph indicator next to the current user's name. The kiosk-
  // stream RR block carries player IDs and usernames but no per-player
  // confirm-before-each-turn data (per-participant flags only flow on
  // the partner-side participant stream which mobile doesn't subscribe
  // to). So we surface the lock for the current user only — they're
  // always in the roster, and their CBT state is sourced from the same
  // hook that drives the toggle on FullSwingHomePage.
  const {confirmBeforeEachTurn} = useConfirmBeforeEachTurn();

  // This panel is an internal debugging surface, not product UI — it must
  // never render for players. Off by default; internal builds can enable
  // it with the ?show_rotation_debug=1 dev override. That override is
  // guarded at runtime by an environment allowlist (see
  // devOverrides.isShowRotationDebugPanel) — NOT tree-shaken out like the
  // NODE_ENV-gated flags, because the panel must stay live in the
  // production-mode playwright bundle. It is off in every deployed/player
  // environment. The gate sits after the hooks so hook order never varies
  // with it.
  if (!isShowRotationDebugPanel()) return null;

  if (!roundRobinStatus || !roundRobinStatus.enabled) return null;

  const {
    currentPlayerId, shotsTakenThisTurn, shotsPerPlayer, shotsRemaining,
    playerOrder, nextPlayerId, turnComplete
  } = roundRobinStatus;

  const currentPlayer = players.find(p => p.id === currentPlayerId);
  const nextPlayer = players.find(p => p.id === nextPlayerId);
  // `user.userId` is proto3 int64 (bigint); `currentPlayerId` is typed as
  // `number` by the local store shim. A naïve `BigInt(number)` silently
  // truncates to 53 bits of precision — fine for the IDs we use today but a
  // correctness trap if a producer ever injects a value > Number.MAX_SAFE_INTEGER.
  // String comparison stays correct regardless of each side's concrete type.
  const isMyTurn = user?.userId !== undefined && String(user.userId) === String(currentPlayerId);
  const progressPercent = shotsPerPlayer > 0 ? (shotsTakenThisTurn / shotsPerPlayer) * 100 : 0;

  // Target preview for the current user. The two rows are sourced from
  // different streams to match how each value evolves:
  //
  //  - "Target: Ny" (current shot) reads `bet.configuredDistanceFromPin`,
  //    which is constant for the lifetime of an accepted bet — a snapshot
  //    is fine because the value does not change once the bet is created.
  //
  //  - "Your next target: Ny" (next shot preview) reads
  //    `userSessionData.previewedNextTargetYards` from the live kiosk
  //    stream. Partner-service writes this field on every BET_FILLED /
  //    BET_RESOLVED tick and the participant subscription pushes each
  //    update to Context (see Main.tsx). Reading from `bet` here would
  //    pin the preview to whatever value was on the locally-cached bet
  //    when GetBets last succeeded — that record is not refreshed
  //    between shots (see BetSlip's GetBets retry-loop, which clears its
  //    timer on the first accepted bet found), producing the customer's
  //    "looping the same N over and over" symptom (SMG-2480 / FSG-8224).
  //
  //    Context updates are debounced through `userSessionDataEquals`
  //    (Main.tsx) — that equality check filters out ticks whose tracked
  //    fields haven't changed. `previewedNextTargetYards` is currently
  //    in that comparator (Main.tsx:86); a future field added here MUST
  //    also be added to `userSessionDataEquals`, otherwise the kiosk
  //    stream will silently drop those updates and reproduce the same
  //    class of stale-value bug.
  //
  // For other players' turns we hide both rows — the current user's bet
  // and preview are private to them.
  //
  // Both fields are proto3 int64 with no `optional` modifier, so their
  // zero value (0n / '0' / Long.ZERO, depending on the proto-to-TS
  // generator) is indistinguishable from "unset" on the wire. The proto
  // explicitly documents `previewed_next_target_yards = 0` and
  // `next_shot_target_yards = 0` as "unavailable" and a
  // `configured_distance_from_pin` of 0 yards is not a real challenge
  // target — both are treated as "hide". `isNonZeroInt64` compares
  // Number() so the check is correct regardless of whether the generator
  // emits bigint, number, string, or Long.
  const nextShotTargetYards = isMyTurn && isNonZeroInt64(userSessionData?.previewedNextTargetYards)
    ? Number(userSessionData!.previewedNextTargetYards)
    : null;
  const currentTargetYards = isMyTurn && isNonZeroInt64(bet?.configuredDistanceFromPin)
    ? Number(bet!.configuredDistanceFromPin)
    : null;

  return (
    <Div testID="rr-turn-display" mx={16} mt={8} rounded={12} bg="#1A1A1B" borderWidth={1} borderColor="#333335" overflow="hidden">
      {/* Waiting line — when it isn't our turn, lead the panel with an
          explicit message naming the active player. Renders alongside the
          existing turn display, not instead of, so the user still has full
          context of where rotation stands. */}
      {!isMyTurn && (
        <Div px={16} pt={12}>
          <P testID="rr-waiting-message" fontFamily="DavisSans-Regular" fs={13} color="#BBBCBF">
            Waiting for {playerLabel(currentPlayer, currentPlayerId)} to play
          </P>
        </Div>
      )}

      {/* Current Player Header */}
      <Div bg="#252526" px={16} py={12} row alignItems="center" justifyContent="space-between">
        <Div testID="rr-player-current" row alignItems="center" gap={10}>
          <FontAwesome name="user-circle" size={28} color={isMyTurn ? "#1E9428" : "#3369FF"}/>
          <Div>
            <P fontFamily="DavisSans-Medium" fs={18}>
              {playerLabel(currentPlayer, currentPlayerId)}
            </P>
            <Div row alignItems="center" gap={6} mt={2}>
              <Div bg={isMyTurn ? "#1E9428" : "#3369FF"} px={8} py={2} rounded={4}
                   accessibilityRole="header"
                   accessibilityLabel={`Current turn: ${playerLabel(currentPlayer, currentPlayerId)}`}>
                <P fontFamily="DavisSans-Medium" fs={11} color="white">
                  {isMyTurn ? "YOUR TURN" : "NOW PLAYING"}
                </P>
              </Div>
              {currentTargetYards !== null && (
                <P testID="rr-current-target" fontFamily="DavisSans-Regular" fs={12} color="#BBBCBF">
                  Target: {currentTargetYards}y
                </P>
              )}
            </Div>
          </Div>
        </Div>

        {turnComplete && (
          <Div testID="rr-turn-complete" bg="#1E9428" px={10} py={4} rounded={6}>
            <P fontFamily="DavisSans-Medium" fs={12}>TURN COMPLETE</P>
          </Div>
        )}
      </Div>

      {/* Shot Counter */}
      <Div px={16} pt={14} pb={8}>
        <Div row justifyContent="space-between" alignItems="center" mb={8}>
          <P testID="rr-shot-counter" fontFamily="DavisSans-Regular" fs={14} color="#BBBCBF"
             accessibilityRole="text"
             accessibilityLabel={`Shot ${shotsTakenThisTurn + 1 > shotsPerPlayer ? shotsPerPlayer : shotsTakenThisTurn + 1} of ${shotsPerPlayer}`}>
            Shot {shotsTakenThisTurn + 1 > shotsPerPlayer ? shotsPerPlayer : shotsTakenThisTurn + 1} of {shotsPerPlayer}
          </P>
          <P testID="rr-shots-remaining" fontFamily="DavisSans-Medium" fs={14} color="#3369FF"
             accessibilityRole="text"
             accessibilityLabel={`${shotsRemaining} shots remaining`}>
            {shotsRemaining} remaining
          </P>
        </Div>

        {/* Progress Bar */}
        <Div h={6} bg="#333335" rounded={3} overflow="hidden">
          <View style={{
            height: 6,
            width: `${Math.min(progressPercent, 100)}%`,
            backgroundColor: isMyTurn ? '#1E9428' : '#3369FF',
            borderRadius: 3
          }}/>
        </Div>
      </Div>

      {/* Up-next player (rotation) and the current user's own next-shot
          target (own-bet preview) are two independent facts. Render them
          on separate rows so the visual layout does not imply "Bob's
          target is 18y" — the target is always the current user's. */}
      {nextPlayer && !turnComplete && (
        <Div testID="rr-next-player-target" px={16} pb={12} pt={4}>
          <Div h={1} bg="#333335" mb={10}/>
          <Div row alignItems="center" gap={8}>
            <MaterialCommunityIcons name="arrow-right-circle-outline" size={20} color="#BBBCBF"/>
            <P fontFamily="DavisSans-Regular" fs={13} color="#BBBCBF">
              Up next: {playerLabel(nextPlayer, nextPlayerId)}
            </P>
          </Div>
          {nextShotTargetYards !== null && (
            <Div row alignItems="center" gap={8} mt={6}>
              <MaterialCommunityIcons name="target" size={18} color="#BBBCBF"/>
              {/* testID anchors the harness's two-axis assertion for
                  SMG-2480 / FSG-8224. On Expo Web `testID` is rendered
                  as `data-testid`; Playwright's `page.getByTestId()`
                  reads that attribute. The wire→render contract is
                  "this text matches the latest tick's
                  `userSessionData.previewedNextTargetYards` while the
                  user's turn is active". */}
              <P testID="rr-next-shot-target" fontFamily="DavisSans-Regular" fs={13} color="#BBBCBF">
                Your next target: {nextShotTargetYards}y
              </P>
            </Div>
          )}
        </Div>
      )}

      {/* Player Order List */}
      {playerOrder.length > 1 && (
        <Div testID="rr-player-order-list" accessibilityRole="list" px={16} pb={14}>
          <Div h={1} bg="#333335" mb={10}/>
          <P fontFamily="DavisSans-Medium" fs={12} color="#BBBCBF" mb={8}>PLAYER ORDER</P>
          <Div row flexWrap="wrap" gap={8}>
            {(() => {
              const currentIndex = playerOrder.indexOf(currentPlayerId);
              return playerOrder.map((playerId, index) => {
                const player = players.find(p => p.id === playerId);
                const isCurrent = playerId === currentPlayerId;
                const isMe = user?.userId !== undefined && String(user.userId) === String(playerId);
                // Lock glyph appears for the current user only — see the
                // hook reasoning at the top of the component. Other players'
                // CBT state isn't on the wire to mobile.
                const showLock = isMe && confirmBeforeEachTurn;

                // shotsUntilTurn — how many shots remain before this player's
                // turn starts. Formula:
                //   positionsAhead = (index - currentIndex + N) % N
                //   shotsUntilTurn = shotsRemaining + (positionsAhead - 1) * shotsPerPlayer
                // Reasoning: the current player has `shotsRemaining` left
                // in this turn. The slot immediately after current (positionsAhead=1)
                // therefore waits exactly that many shots. Each later slot
                // adds one full turn-worth of shots (`shotsPerPlayer`).
                // For the current player we render nothing (their badge already
                // says "YOUR TURN" / "NOW PLAYING"). When currentIndex is -1
                // (rotation pointer not in the roster yet — early-boot race)
                // we suppress the countdown entirely rather than print a
                // confusing value.
                let shotsUntilTurn: number | null = null;
                if (!isCurrent && currentIndex >= 0 && shotsPerPlayer > 0) {
                  const N = playerOrder.length;
                  const positionsAhead = ((index - currentIndex) % N + N) % N;
                  shotsUntilTurn = shotsRemaining + (positionsAhead - 1) * shotsPerPlayer;
                }

                return (
                  <Div
                    key={playerId}
                    row alignItems="center" gap={6}
                    bg={isCurrent ? "rgba(51, 105, 255, 0.15)" : "rgba(255, 255, 255, 0.05)"}
                    borderWidth={isCurrent ? 1 : 0}
                    borderColor={isCurrent ? "#3369FF" : "transparent"}
                    px={10} py={6} rounded={8}
                  >
                    <P fontFamily="DavisSans-Medium" fs={12} color={isCurrent ? "#3369FF" : "#BBBCBF"}>
                      {index + 1}.
                    </P>
                    <P fontFamily="DavisSans-Regular" fs={12} color={isCurrent ? "white" : "#BBBCBF"}>
                      {playerLabel(player, playerId)}
                    </P>
                    {shotsUntilTurn !== null && (
                      <P
                        testID={`rr-shots-until-turn-${playerId}`}
                        fontFamily="DavisSans-Regular"
                        fs={11}
                        color="#888A8E"
                        accessibilityLabel={`${shotsUntilTurn} shots until ${playerLabel(player, playerId)}'s turn`}
                      >
                        (in {shotsUntilTurn})
                      </P>
                    )}
                    {showLock && (
                      <FontAwesome
                        testID={`rr-cbt-lock-${playerId}`}
                        name="lock"
                        size={11}
                        color="#FFD166"
                        accessibilityLabel="Confirm before each turn enabled"
                      />
                    )}
                  </Div>
                );
              });
            })()}
          </Div>
        </Div>
      )}
    </Div>
  );
};

export default RoundRobinTurnDisplay;
